/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-prototype-builtins */
/* eslint-disable react/prop-types */
import { useState, useEffect, useCallback, useMemo } from "react";
import useFormData from "@/hooks/useFormData";
import Grid from "@mui/material/Grid2";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Typography,
  IconButton,
  Box,
  Checkbox,
  Paper,
  Divider,
  Chip,
  Fade,
  Card,
  CardContent,
  LinearProgress,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import PsychologyIcon from "@mui/icons-material/Psychology";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import AssignmentIcon from "@mui/icons-material/Assignment";
import useApiErrorHandler from "@/hooks/useApiErrorHandler";
import Toast from "@/components/toast";
import Select from "react-select";
import { getMultiSelectStyles, getSingleSelectStyles } from "@/lib/styles";
import { useTheme } from "@mui/material/styles";
import useGetUsers from "@/hooks/useGetUsers";

import FileUploadField from "@/components/FileUploadField";
import { useTranslation } from "react-i18next";

// MarkdownRenderer component to format AI comment content
const MarkdownRenderer = ({ content }) => {
  if (!content) return null;

  const lines = content.split("\n");
  const elements = [];
  let key = 0;

  lines.forEach((line) => {
    const trimmedLine = line.trim();

    if (!trimmedLine) {
      // Empty line - add spacing
      elements.push(<Box key={key++} sx={{ height: 8 }} />);
      return;
    }

    if (trimmedLine.startsWith("### ")) {
      // Title (h3)
      const title = trimmedLine.substring(4);
      elements.push(
        <Typography
          key={key++}
          variant="h6"
          fontWeight="bold"
          sx={{ mt: 2, mb: 1, color: "primary.main" }}
        >
          {title}
        </Typography>
      );
    } else if (
      trimmedLine.startsWith("**") &&
      trimmedLine.endsWith("**") &&
      trimmedLine.length > 4
    ) {
      // Subtitle (bold text)
      const subtitle = trimmedLine.substring(2, trimmedLine.length - 2);
      elements.push(
        <Typography
          key={key++}
          variant="subtitle1"
          fontWeight="bold"
          sx={{ mt: 1.5, mb: 0.5, color: "text.primary" }}
        >
          {subtitle}
        </Typography>
      );
    } else if (trimmedLine.startsWith("- ")) {
      // Bullet point
      const bulletText = trimmedLine.substring(2);
      elements.push(
        <Box
          key={key++}
          sx={{ display: "flex", alignItems: "flex-start", mb: 0.5, ml: 1 }}
        >
          <Typography
            variant="body2"
            sx={{ mr: 1, color: "primary.main", fontWeight: "bold" }}
          >
            •
          </Typography>
          <Typography variant="body2" sx={{ flex: 1 }}>
            {bulletText}
          </Typography>
        </Box>
      );
    } else {
      // Regular text
      elements.push(
        <Typography
          key={key++}
          variant="body2"
          sx={{ mb: 0.5, lineHeight: 1.6 }}
        >
          {trimmedLine}
        </Typography>
      );
    }
  });

  return <Box>{elements}</Box>;
};

// Helper to format options for react-select
const formatSelectOptions = (options, inputName) => {
  if (!Array.isArray(options)) {
    console.warn(`Options is not an array for input: ${inputName}`, options);
    return [];
  }
  return options.map((option) => ({
    value: option,
    label: option,
  }));
};

function Comment({
  documentId,
  aicomment,
  isCommentDialogOpen,
  setIsCommentDialogOpen,
  currentStepId,
  stepInputs = [],
  onClose,
  stepName,
  onSuccess,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [inputValues, setInputValues] = useState({});
  const [fileInputValues, setFileInputValues] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showAiComment, setShowAiComment] = useState(false);
  const { users } = useGetUsers();
  const { postData, postIsLoading } = useFormData();
  const { handleApiError, toast, setToast, handleToastClose } =
    useApiErrorHandler();

  // Memoize UsersList and select styles
  const UsersList = useMemo(
    () =>
      users?.map(({ id, first_name, last_name }) => ({
        value: id,
        label: `${first_name} ${last_name}`,
      })) || [],
    [users]
  );
  const singleSelectStyles = useMemo(
    () => getSingleSelectStyles(theme),
    [theme]
  );
  const multiSelectStyles = useMemo(() => getMultiSelectStyles(theme), [theme]);

  // Calculate form progress
  const formProgress = useMemo(() => {
    const totalFields = stepInputs.filter((input) => input.required).length;
    if (totalFields === 0) return 100;

    const completedFields = stepInputs.filter((input) => {
      if (!input.required) return true;
      return !validationErrors[input.id];
    }).length;

    return Math.round((completedFields / stepInputs.length) * 100);
  }, [stepInputs, validationErrors]);

  // Initialize input values based on default values from stepInputs
  useEffect(() => {
    if (!stepInputs.length) return;
    if (Object.keys(inputValues).length > 0) return;
    const initialValues = stepInputs.reduce((acc, input) => {
      const type = (input.input_type || "").toUpperCase();
      let value;
      switch (type) {
        case "BOOLEAN":
          // Boolean: Use default_value if provided, otherwise "NO" (false)
          if (input.hasOwnProperty("default_value") && input.default_value) {
            const defaultVal = String(input.default_value).toUpperCase();
            if (
              defaultVal === "YES" ||
              defaultVal === "TRUE" ||
              defaultVal === "1"
            ) {
              value = "YES";
            } else {
              value = "NO"; // Default to NO for any other value
            }
          } else {
            value = "NO"; // No default value - start with NO
          }
          break;
        case "CONFIRMATION":
          // Confirmation always starts with false (unchecked)
          value = false;
          break;
        case "MULTI_USER_SELECTION":
        case "MULTI_SELECTION":
          value =
            input.hasOwnProperty("default_value") && input.default_value != null
              ? Array.isArray(input.default_value)
                ? input.default_value
                : String(input.default_value).split(",").filter(Boolean)
              : [];
          break;
        case "SELECTION":
          value = input.hasOwnProperty("default_value")
            ? input.default_value
            : "";
          break;
        default:
          value = input.hasOwnProperty("default_value")
            ? input.default_value
            : "";
          break;
      }
      acc[input.id] = value;
      return acc;
    }, {});
    setInputValues(initialValues);
  }, [stepInputs]);

  // Validate required fields
  const validateField = useCallback((input, value, files, t) => {
    const type = (input.input_type || "").toUpperCase();

    // CONFIRMATION is ALWAYS required (must be checked)
    if (type === "CONFIRMATION") {
      return value === true
        ? ""
        : t("comment.errorConfirmation", {
            field: input.name,
            defaultValue: `${input.name} must be confirmed`,
          });
    }

    // For other types, check if field is required
    if (!input.required) return "";

    switch (type) {
      case "BOOLEAN":
        return value === "YES" || value === "NO"
          ? ""
          : t("comment.errorBoolean", { field: input.name });
      case "MULTI_USER_SELECTION":
      case "MULTI_SELECTION":
        return Array.isArray(value) && value.length > 0
          ? ""
          : t("comment.errorMultiSelect", { field: input.name });
      case "FILE_UPLOAD":
        return Array.isArray(files) && files.length > 0
          ? ""
          : t("comment.errorFileUpload", { field: input.name });
      case "SHORT_TEXT":
      case "TEXT":
      case "NUMERIC":
      case "TIMESTAMP":
      case "SELECTION":
      case "USER_SELECTION":
        return value && String(value).trim() !== ""
          ? ""
          : t("comment.errorRequired", { field: input.name });
      default:
        return "";
    }
  }, []);

  // Update validation errors
  useEffect(() => {
    const errors = stepInputs.reduce((acc, input) => {
      const type = (input.input_type || "").toUpperCase();
      // Always validate CONFIRMATION types, or validate if field is required
      if (type === "CONFIRMATION" || input.required) {
        const error = validateField(
          input,
          inputValues[input.id],
          fileInputValues[input.id],
          t
        );
        if (error) acc[input.id] = error;
      }
      return acc;
    }, {});
    setValidationErrors(errors);
  }, [inputValues, fileInputValues, stepInputs, validateField, t]);

  // Compute if all required fields are filled
  const allRequiredFilled = useMemo(() => {
    return stepInputs.every((input) => {
      const type = (input.input_type || "").toUpperCase();
      // CONFIRMATION is always required, or check if field is required
      if (type === "CONFIRMATION" || input.required) {
        return !validationErrors[input.id];
      }
      return true;
    });
  }, [validationErrors, stepInputs]);

  // Handle input changes
  const handleInputChange = useCallback((id, value) => {
    setInputValues((prev) => ({
      ...prev,
      [id]: value,
    }));
  }, []);

  // Handle file input changes
  const handleFileInputChange = useCallback(
    (id, files) => {
      const arr = Array.from(files);
      setFileInputValues((prev) => {
        const newValues = { ...prev, [id]: arr };

        // Validate this field immediately
        if (stepInputs.find((input) => input.id === id)?.required) {
          const error = validateField(
            stepInputs.find((input) => input.id === id),
            inputValues[id],
            arr,
            t
          );
          setValidationErrors((prev) => ({
            ...prev,
            [id]: error,
          }));
        }

        return newValues;
      });
    },
    [inputValues, stepInputs, validateField, t]
  );

  // Handle blur to mark fields as touched
  const handleBlur = useCallback((id) => {
    setTouched((prev) => ({ ...prev, [id]: true }));
  }, []);

  // Prepare filled step inputs for submission
  const prepareFilledStepInputs = useCallback(() => {
    return stepInputs
      .map(({ id, input_type }) => {
        let raw_value = inputValues[id];
        let file_handles = (fileInputValues[id] || []).map((f) => f.name);
        const inputTypeUpper = input_type?.toUpperCase();

        if (inputTypeUpper === "TIMESTAMP" && raw_value) {
          raw_value = new Date(raw_value).toISOString();
        }

        // Handle BOOLEAN - convert YES/NO to "true"/"false" strings
        // Always send "true" or "false" (no empty strings)
        if (inputTypeUpper === "BOOLEAN") {
          if (raw_value === "YES") {
            raw_value = "true";
          } else {
            // Default to "false" for NO or any other value
            raw_value = "false";
          }
        }

        // Handle CONFIRMATION - send as "true" or "false" string
        if (inputTypeUpper === "CONFIRMATION") {
          raw_value = raw_value === true ? "true" : "false";
        }

        if (["MULTI_USER_SELECTION"].includes(inputTypeUpper)) {
          raw_value = Array.isArray(raw_value) ? raw_value.join(",") : "";
        } else if (["MULTI_SELECTION"].includes(inputTypeUpper)) {
          raw_value = Array.isArray(raw_value) ? raw_value.join("||") : "";
        } else if (!["BOOLEAN", "CONFIRMATION"].includes(inputTypeUpper)) {
          // Don't convert BOOLEAN or CONFIRMATION to string again
          raw_value = raw_value?.toString() || "";
        }

        return {
          step_input_id: id,
          file_handles: file_handles,
          raw_value,
        };
      })
      .filter((item) => {
        const { raw_value, file_handles } = item;
        // Include items with file handles or non-empty raw_value
        const hasText =
          typeof raw_value === "string" && raw_value.trim().length > 0;
        const hasFiles = Array.isArray(file_handles) && file_handles.length > 0;
        return hasText || hasFiles;
      });
  }, [stepInputs, inputValues, fileInputValues]);

  // Handle form submission
  const handleCommentSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!allRequiredFilled) {
        setTouched(
          stepInputs.reduce((acc, input) => {
            acc[input.id] = true;
            return acc;
          }, {})
        );
        return;
      }

      const form = new FormData();
      const payload = {
        step_id: currentStepId,
        filled_step_inputs: prepareFilledStepInputs(),
      };
      form.append(
        "data",
        new Blob([JSON.stringify(payload)], { type: "application/json" })
      );
      for (const key in fileInputValues) {
        fileInputValues[key].forEach((file) =>
          form.append("step_input_attachments", file)
        );
      }

      try {
        await postData(form, `/api/v1/workpermits/${documentId}/steps`);
        setInputValues({});
        if (onSuccess) {
          onSuccess();
        }
        setIsCommentDialogOpen(false);
        onClose();
        setToast({
          open: true,
          message: t("comment.successMessage"),
          severity: "success",
        });
      } catch (error) {
        setToast({
          open: true,
          message: t("comment.errorMessage", {
            message: error.response?.data?.message || t("comment.defaultError"),
          }),
          severity: "error",
        });
      } finally {
        setIsCommentDialogOpen(false);
      }
    },
    [
      allRequiredFilled,
      currentStepId,
      documentId,
      fileInputValues,
      handleApiError,
      onClose,
      onSuccess,
      postData,
      prepareFilledStepInputs,
      setIsCommentDialogOpen,
      setToast,
      stepInputs,
      t,
    ]
  );

  // Render input based on type
  const renderInputField = useCallback(
    (input) => {
      const type = (input.input_type || "").toUpperCase();
      const error = touched[input.id] && validationErrors[input.id];
      // Check if field should show validation (CONFIRMATION is always required)
      const isValid =
        touched[input.id] &&
        !validationErrors[input.id] &&
        (type === "CONFIRMATION" || input.required);

      const fieldWrapper = (content) => (
        <Card
          key={input.id}
          sx={{
            mb: 2,
            border: `1px solid ${error ? theme.palette.error.main : theme.palette.checklist.border.subtle}`,
            bgcolor: theme.palette.checklist.surface.elevated,
            borderRadius: 2,
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              borderColor: error
                ? theme.palette.error.main
                : theme.palette.checklist.border.focus,
              boxShadow: theme.shadows[1],
            },
          }}
        >
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {input.name}
                {input.required && (
                  <span style={{ color: theme.palette.error.main }}> *</span>
                )}
              </Typography>
              {isValid && (
                <CheckCircleIcon
                  sx={{
                    ml: 1,
                    fontSize: 16,
                    color: theme.palette.success.main,
                  }}
                />
              )}
              {error && (
                <WarningIcon
                  sx={{
                    ml: 1,
                    fontSize: 16,
                    color: theme.palette.error.main,
                  }}
                />
              )}
            </Box>
            {content}
          </CardContent>
        </Card>
      );

      switch (type) {
        case "BOOLEAN":
          return (
            <Card
              key={input.id}
              sx={{
                mb: 2,
                border: `1px solid ${error ? theme.palette.error.main : theme.palette.checklist.border.subtle}`,
                bgcolor: theme.palette.checklist.surface.elevated,
                borderRadius: 2,
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  borderColor: error
                    ? theme.palette.error.main
                    : theme.palette.checklist.border.focus,
                  boxShadow: theme.shadows[1],
                },
              }}
            >
              <CardContent sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {input.name}
                      {input.required && (
                        <span style={{ color: theme.palette.error.main }}>
                          {" "}
                          *
                        </span>
                      )}
                    </Typography>
                    {isValid && (
                      <CheckCircleIcon
                        sx={{
                          ml: 1,
                          fontSize: 16,
                          color: theme.palette.success.main,
                        }}
                      />
                    )}
                    {error && (
                      <WarningIcon
                        sx={{
                          ml: 1,
                          fontSize: 16,
                          color: theme.palette.error.main,
                        }}
                      />
                    )}
                  </Box>

                  <ToggleButtonGroup
                    value={inputValues[input.id] || null}
                    exclusive
                    onChange={(e, newValue) => {
                      handleInputChange(input.id, newValue);
                    }}
                    onBlur={() => handleBlur(input.id)}
                    sx={{ gap: 0.5 }}
                  >
                    <ToggleButton
                      value="YES"
                      aria-label="yes"
                      sx={{
                        px: 2,
                        py: 0.5,
                        borderRadius: 3,
                        fontWeight: 500,
                        textTransform: "none",
                        minWidth: 60,
                        fontSize: "0.8125rem",
                        transition: "all 0.3s ease",
                        border: `1px solid ${theme.palette.checklist.border.subtle}`,
                        bgcolor:
                          inputValues[input.id] === "YES"
                            ? theme.palette.success.dark
                            : theme.palette.checklist.surface.elevated,
                        color:
                          inputValues[input.id] === "YES"
                            ? theme.palette.common.white
                            : theme.palette.text.primary,
                        "&:hover": {
                          bgcolor: theme.palette.success.main,
                          color: theme.palette.common.white,
                        },
                        "&.Mui-selected": {
                          bgcolor: theme.palette.success.dark,
                          color: theme.palette.common.white,
                          boxShadow: theme.shadows[2],
                          transform: "scale(1.05)",
                          borderColor: theme.palette.checklist.border.focus,
                          "&:hover": {
                            bgcolor: theme.palette.success.main,
                          },
                        },
                      }}
                    >
                      {t("stepInputsForm.yes", "YES")}
                    </ToggleButton>
                    <ToggleButton
                      value="NO"
                      aria-label="no"
                      sx={{
                        px: 2,
                        py: 0.5,
                        borderRadius: 3,
                        fontWeight: 500,
                        textTransform: "none",
                        minWidth: 60,
                        fontSize: "0.8125rem",
                        transition: "all 0.3s ease",
                        border: `1px solid ${theme.palette.checklist.border.subtle}`,
                        bgcolor:
                          inputValues[input.id] === "NO"
                            ? theme.palette.error.dark
                            : theme.palette.checklist.surface.elevated,
                        color:
                          inputValues[input.id] === "NO"
                            ? theme.palette.common.white
                            : theme.palette.text.primary,
                        "&:hover": {
                          bgcolor: theme.palette.error.main,
                          color: theme.palette.common.white,
                        },
                        "&.Mui-selected": {
                          bgcolor: theme.palette.error.dark,
                          color: theme.palette.common.white,
                          boxShadow: theme.shadows[2],
                          transform: "scale(1.05)",
                          borderColor: theme.palette.checklist.border.focus,
                          "&:hover": {
                            bgcolor: theme.palette.error.main,
                          },
                        },
                      }}
                    >
                      {t("stepInputsForm.no", "NO")}
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                {input.description && (
                  <Typography
                    variant="body2"
                    sx={{ mt: 1, color: "text.secondary" }}
                  >
                    {input.description}
                  </Typography>
                )}

                {error && (
                  <Typography
                    variant="caption"
                    sx={{ mt: 0.5, color: "error.main", display: "block" }}
                  >
                    {error}
                  </Typography>
                )}
              </CardContent>
            </Card>
          );
        case "CONFIRMATION":
          return (
            <Card
              key={input.id}
              sx={{
                mb: 2,
                border: `1px solid ${error ? theme.palette.error.main : theme.palette.checklist.border.subtle}`,
                bgcolor: theme.palette.checklist.surface.elevated,
                borderRadius: 2,
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  borderColor: error
                    ? theme.palette.error.main
                    : theme.palette.checklist.border.focus,
                  boxShadow: theme.shadows[1],
                },
              }}
            >
              <CardContent sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {input.name}
                      <span style={{ color: theme.palette.error.main }}>
                        {" "}
                        * {/* CONFIRMATION is always required */}
                      </span>
                    </Typography>
                    {isValid && (
                      <CheckCircleIcon
                        sx={{
                          ml: 1,
                          fontSize: 16,
                          color: theme.palette.success.main,
                        }}
                      />
                    )}
                    {error && (
                      <WarningIcon
                        sx={{
                          ml: 1,
                          fontSize: 16,
                          color: theme.palette.error.main,
                        }}
                      />
                    )}
                  </Box>

                  <Checkbox
                    checked={Boolean(inputValues[input.id])}
                    onChange={(e) =>
                      handleInputChange(input.id, e.target.checked)
                    }
                    onBlur={() => handleBlur(input.id)}
                    sx={{
                      "&.Mui-checked": {
                        color: theme.palette.primary.main,
                      },
                    }}
                  />
                </Box>

                {input.description && (
                  <Typography
                    variant="body2"
                    sx={{ mt: 1, color: "text.secondary" }}
                  >
                    {input.description}
                  </Typography>
                )}

                {error && (
                  <Typography
                    variant="caption"
                    sx={{ mt: 0.5, color: "error.main", display: "block" }}
                  >
                    {error}
                  </Typography>
                )}
              </CardContent>
            </Card>
          );
        case "SHORT_TEXT":
        case "TEXT":
          return fieldWrapper(
            <TextField
              fullWidth
              multiline
              rows={input.input_type === "TEXT" ? 3 : 1}
              value={inputValues[input.id] || ""}
              onChange={(e) => handleInputChange(input.id, e.target.value)}
              onBlur={() => handleBlur(input.id)}
              variant="outlined"
              size="small"
              required={input.required}
              error={!!error}
              helperText={error}
              placeholder={t("comment.textFieldPlaceholder")}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: theme.palette.checklist.surface.primary,
                },
              }}
            />
          );
        case "NUMERIC":
          return fieldWrapper(
            <TextField
              fullWidth
              type="number"
              value={inputValues[input.id] || ""}
              onChange={(e) => handleInputChange(input.id, e.target.value)}
              onBlur={() => handleBlur(input.id)}
              variant="outlined"
              size="small"
              required={input.required}
              error={!!error}
              helperText={error}
              placeholder={t("comment.numericFieldPlaceholder")}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: theme.palette.checklist.surface.primary,
                },
              }}
            />
          );
        case "TIMESTAMP":
          return fieldWrapper(
            <TextField
              fullWidth
              type="datetime-local"
              value={inputValues[input.id] || ""}
              onChange={(e) => handleInputChange(input.id, e.target.value)}
              onBlur={() => handleBlur(input.id)}
              variant="outlined"
              size="small"
              required={input.required}
              slotProps={{ inputLabel: { shrink: true } }}
              error={!!error}
              helperText={error}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: theme.palette.checklist.surface.primary,
                },
              }}
            />
          );
        case "USER_SELECTION":
          return fieldWrapper(
            <Select
              styles={{
                ...singleSelectStyles,
                menuPortal: (base) => ({ ...base, zIndex: 1301 }),
                control: (base, state) => ({
                  ...base,
                  backgroundColor: theme.palette.checklist.surface.primary,
                  borderColor: error
                    ? theme.palette.error.main
                    : state.isFocused
                      ? theme.palette.primary.main
                      : theme.palette.checklist.border.subtle,
                }),
              }}
              options={UsersList}
              value={
                UsersList.find((opt) => opt.value === inputValues[input.id]) ||
                null
              }
              onChange={(selectedOption) =>
                handleInputChange(
                  input.id,
                  selectedOption ? selectedOption.value : ""
                )
              }
              onBlur={() => handleBlur(input.id)}
              placeholder={t("comment.selectUserPlaceholder")}
              menuPortalTarget={
                typeof document !== "undefined" ? document.body : null
              }
              menuPosition="fixed"
              required={input.required}
            />
          );
        case "MULTI_USER_SELECTION":
          return fieldWrapper(
            <Select
              isMulti
              styles={{
                ...multiSelectStyles,
                menuPortal: (base) => ({ ...base, zIndex: 1301 }),
                control: (base, state) => ({
                  ...base,
                  backgroundColor: theme.palette.checklist.surface.primary,
                  borderColor: error
                    ? theme.palette.error.main
                    : state.isFocused
                      ? theme.palette.primary.main
                      : theme.palette.checklist.border.subtle,
                }),
              }}
              options={UsersList}
              closeMenuOnSelect={false}
              value={
                Array.isArray(inputValues[input.id])
                  ? inputValues[input.id]
                      .map((val) => UsersList.find((opt) => opt.value === val))
                      .filter(Boolean)
                  : []
              }
              onChange={(selectedOptions) =>
                handleInputChange(
                  input.id,
                  selectedOptions ? selectedOptions.map((opt) => opt.value) : []
                )
              }
              onBlur={() => handleBlur(input.id)}
              placeholder={t("comment.selectUsersPlaceholder")}
              menuPortalTarget={
                typeof document !== "undefined" ? document.body : null
              }
              menuPosition="fixed"
              required={input.required}
            />
          );
        case "SELECTION": {
          const selectOptions = formatSelectOptions(input.options, input.name);
          if (!selectOptions.length) {
            return fieldWrapper(
              <Typography color="error" variant="body2">
                {t("comment.noOptions", { field: input.name })}
              </Typography>
            );
          }
          return fieldWrapper(
            <Select
              styles={{
                ...singleSelectStyles,
                menuPortal: (base) => ({ ...base, zIndex: 1301 }),
                control: (base, state) => ({
                  ...base,
                  backgroundColor: theme.palette.checklist.surface.primary,
                  borderColor: error
                    ? theme.palette.error.main
                    : state.isFocused
                      ? theme.palette.primary.main
                      : theme.palette.checklist.border.subtle,
                }),
              }}
              options={selectOptions}
              value={
                selectOptions.find(
                  (opt) => opt.value === inputValues[input.id]
                ) || null
              }
              onChange={(selectedOption) =>
                handleInputChange(
                  input.id,
                  selectedOption ? selectedOption.value : ""
                )
              }
              onBlur={() => handleBlur(input.id)}
              placeholder={t("comment.selectOptionPlaceholder")}
              menuPortalTarget={
                typeof document !== "undefined" ? document.body : null
              }
              menuPosition="fixed"
              isDisabled={!selectOptions.length}
              required={input.required}
            />
          );
        }
        case "MULTI_SELECTION": {
          const multiSelectOptions = formatSelectOptions(
            input.options,
            input.name
          );
          if (!multiSelectOptions.length) {
            return fieldWrapper(
              <Typography color="error" variant="body2">
                {t("comment.noOptions", { field: input.name })}
              </Typography>
            );
          }
          return fieldWrapper(
            <Select
              isMulti
              styles={{
                ...multiSelectStyles,
                menuPortal: (base) => ({ ...base, zIndex: 1301 }),
                control: (base, state) => ({
                  ...base,
                  backgroundColor: theme.palette.checklist.surface.primary,
                  borderColor: error
                    ? theme.palette.error.main
                    : state.isFocused
                      ? theme.palette.primary.main
                      : theme.palette.checklist.border.subtle,
                }),
              }}
              options={multiSelectOptions}
              closeMenuOnSelect={false}
              value={
                Array.isArray(inputValues[input.id])
                  ? inputValues[input.id]
                      .map((val) =>
                        multiSelectOptions.find((opt) => opt.value === val)
                      )
                      .filter(Boolean)
                  : []
              }
              onChange={(selectedOptions) =>
                handleInputChange(
                  input.id,
                  selectedOptions ? selectedOptions.map((opt) => opt.value) : []
                )
              }
              onBlur={() => handleBlur(input.id)}
              placeholder={t("comment.selectOptionsPlaceholder")}
              menuPortalTarget={
                typeof document !== "undefined" ? document.body : null
              }
              menuPosition="fixed"
              isDisabled={!multiSelectOptions.length}
              required={input.required}
            />
          );
        }
        case "FILE_UPLOAD":
          return fieldWrapper(
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: theme.palette.checklist.surface.primary,
                border: `2px dashed ${error ? theme.palette.error.main : theme.palette.checklist.border.subtle}`,
                borderRadius: 1,
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: error
                    ? theme.palette.error.main
                    : theme.palette.checklist.border.focus,
                },
              }}
            >
              <FileUploadField
                multiple={true}
                accept="image/*,application/pdf"
                label={
                  input.display_name ||
                  input.name ||
                  t("comment.uploadFilesLabel")
                }
                inputId={`file-upload-${input.id}`}
                fileNames={(fileInputValues[input.id] || []).map((f) => f.name)}
                onChange={(e) =>
                  handleFileInputChange(input.id, e.target.files)
                }
                onDelete={(fileIndex) => {
                  const updatedFiles = fileInputValues[input.id].filter(
                    (_, idx) => idx !== fileIndex
                  );
                  setFileInputValues((prev) => ({
                    ...prev,
                    [input.id]: updatedFiles,
                  }));
                  // Mark as touched and validate
                  setTouched((prev) => ({ ...prev, [input.id]: true }));
                  const inputField = stepInputs.find((i) => i.id === input.id);
                  if (inputField?.required) {
                    const error = validateField(
                      inputField,
                      inputValues[input.id],
                      updatedFiles,
                      t
                    );
                    setValidationErrors((prev) => ({
                      ...prev,
                      [input.id]: error,
                    }));
                  }
                }}
                required={input.required}
              />
            </Paper>
          );
        default:
          return fieldWrapper(
            <Typography color="error" variant="body2">
              {t("comment.unsupportedInputType", { type: input.input_type })}
            </Typography>
          );
      }
    },
    [
      inputValues,
      fileInputValues,
      handleInputChange,
      handleFileInputChange,
      handleBlur,
      UsersList,
      singleSelectStyles,
      multiSelectStyles,
      t,
      touched,
      validationErrors,
      theme,
      stepInputs,
      validateField,
      setValidationErrors,
      setTouched,
    ]
  );

  // Sort stepInputs by position for rendering
  const sortedStepInputs = useMemo(
    () => [...stepInputs].sort((a, b) => a.position - b.position),
    [stepInputs]
  );

  return (
    <>
      <Dialog
        sx={{
          "& .MuiDialog-paper": {
            width: "60%",
            maxWidth: 800,
            maxHeight: "85vh",
            borderRadius: 2,
            bgcolor: theme.palette.checklist.surface.elevated,
            transition: "all 0.3s ease",
          },
        }}
        maxWidth="md"
        open={isCommentDialogOpen}
        onClose={() => setIsCommentDialogOpen(false)}
      >
        <DialogTitle
          sx={{
            bgcolor: theme.palette.checklist.surface.subtle,
            borderBottom: 1,
            borderColor: theme.palette.checklist.border.subtle,
            p: 3,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AssignmentIcon sx={{ color: theme.palette.primary.main }} />
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  {stepName
                    ? t("comment.dialogTitle", { action: stepName })
                    : t("comment.defaultTitle")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("comment.formProgress", {
                    completed: Object.keys(inputValues).length,
                    total: stepInputs.length,
                  })}
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={() => setIsCommentDialogOpen(false)}
              sx={{
                bgcolor: theme.palette.checklist.interactive.hover,
                "&:hover": {
                  bgcolor: theme.palette.checklist.interactive.selected,
                },
              }}
              aria-label={t("comment.closeDialogAria")}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Progress Bar */}
          {stepInputs.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography variant="caption" color="text.secondary">
                  {t("comment.progressLabel")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formProgress}% {t("comment.complete")}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={formProgress}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  bgcolor: theme.palette.checklist.border.subtle,
                }}
              />
            </Box>
          )}
        </DialogTitle>

        <DialogContent
          sx={{
            p: 3,
            bgcolor: theme.palette.checklist.surface.primary,
          }}
        >
          {stepInputs.length === 0 ? (
            <Box
              sx={{
                textAlign: "center",
                py: 4,
                bgcolor: theme.palette.checklist.empty.background,
                borderRadius: 1,
                border: `2px dashed ${theme.palette.checklist.empty.border}`,
              }}
            >
              <AssignmentIcon
                sx={{
                  fontSize: 48,
                  color: theme.palette.checklist.empty.icon,
                  mb: 2,
                }}
              />
              <Typography
                variant="h6"
                sx={{ color: theme.palette.checklist.empty.text, mb: 1 }}
              >
                {t("comment.noFieldsTitle")}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: theme.palette.checklist.empty.text }}
              >
                {t("comment.noFieldsDescription")}
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {sortedStepInputs.map((input, index) => (
                <Grid size={12} key={input.id}>
                  <Fade in timeout={300 + index * 100}>
                    <Box>{renderInputField(input)}</Box>
                  </Fade>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>

        {aicomment && (
          <>
            <Divider />
            <Box sx={{ p: 3, bgcolor: theme.palette.grey[50] }}>
              <Button
                onClick={() => setShowAiComment(!showAiComment)}
                variant={showAiComment ? "contained" : "outlined"}
                startIcon={<PsychologyIcon />}
                fullWidth
                sx={{
                  borderRadius: 3,
                  textTransform: "none",
                  py: 1.5,
                  transition: "all 0.2s ease",
                  ...(showAiComment
                    ? {
                        background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        boxShadow: theme.shadows[3],
                      }
                    : {
                        borderColor: theme.palette.primary.main,
                        "&:hover": {
                          borderColor: theme.palette.primary.dark,
                          backgroundColor: `${theme.palette.primary.main}08`,
                        },
                      }),
                }}
              >
                {showAiComment
                  ? t("comment.hideAiComment")
                  : t("comment.showAiComment")}
              </Button>
            </Box>
          </>
        )}

        <DialogActions
          sx={{
            p: 3,
            gap: 2,
            bgcolor: theme.palette.checklist.surface.subtle,
            borderTop: 1,
            borderColor: theme.palette.checklist.border.subtle,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
            {allRequiredFilled ? (
              <Chip
                icon={<CheckCircleIcon />}
                label={t("comment.allFieldsComplete")}
                color="success"
                variant="filled"
                sx={{ borderRadius: 2 }}
              />
            ) : (
              <Chip
                icon={<WarningIcon />}
                label={t("comment.fieldsIncomplete")}
                color="warning"
                variant="outlined"
                sx={{ borderRadius: 2 }}
              />
            )}
          </Box>

          <Button
            onClick={() => setIsCommentDialogOpen(false)}
            variant="outlined"
            sx={{
              borderRadius: 2,
              textTransform: "none",
              px: 3,
            }}
          >
            {t("comment.cancelButton")}
          </Button>
          <Button
            onClick={handleCommentSubmit}
            color="primary"
            variant="contained"
            disabled={postIsLoading || !allRequiredFilled}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              px: 4,
              fontWeight: 600,
            }}
          >
            {postIsLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={16} color="inherit" />
                {t("comment.submitting")}
              </Box>
            ) : (
              t("comment.submitButton")
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showAiComment}
        onClose={() => setShowAiComment(false)}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 4,
              boxShadow: theme.shadows[20],
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.info.main}15 0%, ${theme.palette.info.main}25 100%)`,
            borderBottom: `1px solid ${theme.palette.divider}`,
            p: 3,
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background: `linear-gradient(90deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`,
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 48,
                height: 48,
                borderRadius: "50%",
                background: `linear-gradient(135deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`,
                color: "white",
                boxShadow: theme.shadows[3],
              }}
            >
              <SmartToyIcon fontSize="medium" />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h5"
                component="h2"
                fontWeight="bold"
                color="info.main"
              >
                {t("comment.aiCommentTitle")}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {t("comment.aiCommentSubtitle")}
              </Typography>
            </Box>
            <IconButton
              onClick={() => setShowAiComment(false)}
              sx={{
                bgcolor: "rgba(0,0,0,0.04)",
                "&:hover": { bgcolor: "rgba(0,0,0,0.08)" },
                borderRadius: 2,
              }}
              aria-label={t("comment.closeAiDialogAria")}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              backgroundColor: theme.palette.grey[50],
              borderRadius: 2,
              border: `1px solid ${theme.palette.grey[200]}`,
            }}
          >
            <MarkdownRenderer content={aicomment || t("comment.noAiComment")} />
          </Paper>
        </DialogContent>
        <DialogActions
          sx={{ p: 3, borderTop: `1px solid ${theme.palette.divider}` }}
        >
          <Button
            onClick={() => setShowAiComment(false)}
            variant="contained"
            sx={{
              borderRadius: 3,
              textTransform: "none",
              px: 4,
              py: 1,
              background: `linear-gradient(45deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`,
              boxShadow: theme.shadows[3],
              "&:hover": {
                background: `linear-gradient(45deg, ${theme.palette.info.dark}, ${theme.palette.info.main})`,
                boxShadow: theme.shadows[6],
              },
            }}
          >
            {t("comment.closeButton")}
          </Button>
        </DialogActions>
      </Dialog>

      <Toast
        open={toast.open}
        onClose={handleToastClose}
        severity={toast.severity}
        message={toast.message}
      />
    </>
  );
}

export default Comment;
